import PageToolbar from '@/components/linear/page-toolbar';
import { formatRelativeTime } from '@/lib/time';
import {
  editProjectPath,
  projectArchivePath,
  projectStatusPath,
  projectsPath,
} from '@/lib/linear-paths';
import AiProposalsCard from '@/pages/projects/components/ai-proposals-card';
import CapturesCard from '@/pages/projects/components/captures-card';
import NotesCard from '@/pages/projects/components/notes-card';
import PullRequestsCard from '@/pages/projects/components/pull-requests-card';
import RoadmapCard from '@/pages/projects/components/roadmap-card';
import StatusForm from '@/pages/projects/components/status-form';
import TodosCard from '@/pages/projects/components/todos-card';
import type { AiProposal } from '@/types/ai-proposal';
import type { Capture } from '@/types/capture';
import type { Milestone } from '@/types/milestone';
import type { Note } from '@/types/note';
import type { Project } from '@/types/project';
import type { PullRequestItem } from '@/types/pull-request-item';
import type { Todo } from '@/types/todo';
import { Link, router } from '@inertiajs/react';
import { useState, type ReactNode } from 'react';

interface Props {
  project: Project;
  milestones: Milestone[];
  todos: Todo[];
  notes: Note[];
  captures: Capture[];
  pullRequestItems?: PullRequestItem[];
  aiProposals: AiProposal[];
}

interface ProjectProperty {
  label: string;
  value: string;
}

type ProjectTab = 'issues' | 'roadmap' | 'todos' | 'notes' | 'status';

export default function Show({
  project,
  milestones,
  todos,
  notes,
  captures,
  pullRequestItems = [],
  aiProposals = [],
}: Props) {
  const [tab, setTab] = useState<ProjectTab>('issues');
  const properties = projectProperties(project, milestones);

  const handleArchive = () => {
    router.post(projectArchivePath(project.id));
  };

  const handleRestore = () => {
    router.delete(projectArchivePath(project.id));
  };

  return (
    <div className="ln-page">
      <PageToolbar
        title={project.name}
        tabs={
          <div className="ln-tabs">
            <TabButton active={tab === 'issues'} onClick={() => setTab('issues')}>
              Overview
            </TabButton>
            <TabButton active={tab === 'roadmap'} onClick={() => setTab('roadmap')}>
              Roadmap
            </TabButton>
            <TabButton active={tab === 'todos'} onClick={() => setTab('todos')}>
              Todos
            </TabButton>
            <TabButton active={tab === 'notes'} onClick={() => setTab('notes')}>
              Notes
            </TabButton>
            <TabButton active={tab === 'status'} onClick={() => setTab('status')}>
              Status
            </TabButton>
          </div>
        }
        actions={
          <>
            {project.archived ? <span className="ln-label">Archived</span> : null}
            {project.statusBlocked ? (
              <span className="ln-label" style={{ color: '#eb5757' }}>
                Blocked
              </span>
            ) : null}
            <Link href={editProjectPath(project.id)} className="ln-toolbar-btn">
              Edit
            </Link>
            {project.archived ? (
              <button type="button" className="ln-toolbar-btn" onClick={handleRestore}>
                Restore
              </button>
            ) : (
              <button type="button" className="ln-toolbar-btn" onClick={handleArchive}>
                Archive
              </button>
            )}
            <Link href={projectsPath()} className="ln-toolbar-btn">
              All projects
            </Link>
          </>
        }
        showNew={false}
      />

      <div className="ln-page-scroll" style={{ padding: 20 }}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <span
              aria-hidden="true"
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: project.archived ? 'var(--ln-faint)' : 'var(--ln-accent)',
                color: '#ffffff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.75rem',
                fontWeight: 590,
                flexShrink: 0,
              }}
            >
              {projectInitials(project.name)}
            </span>
            <div style={{ minWidth: 0 }}>
              <h1
                style={{
                  margin: 0,
                  fontSize: '1.75rem',
                  fontWeight: 650,
                  lineHeight: 1.15,
                  letterSpacing: '-0.02em',
                }}
              >
                {project.name}
              </h1>
              {project.endState ? (
                <p
                  style={{
                    margin: '6px 0 0',
                    fontSize: '0.9375rem',
                    lineHeight: 1.5,
                    color: 'var(--ln-text)',
                  }}
                >
                  {project.endState}
                </p>
              ) : null}
            </div>
          </div>
          <div className="ln-property" style={{ marginTop: 12, paddingLeft: 0, paddingRight: 0 }}>
            <span className="ln-property-label">Properties</span>
            <div
              style={{
                display: 'flex',
                flex: 1,
                flexWrap: 'wrap',
                gap: '4px 14px',
                minWidth: 0,
              }}
            >
              {properties.map((property) => (
                <PropertyFact key={property.label} label={property.label} value={property.value} />
              ))}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <AiProposalsCard proposals={aiProposals} />
          {tab === 'issues' ? <TodosCard projectId={project.id} todos={todos} /> : null}

          {tab === 'roadmap' ? (
            <RoadmapCard projectId={project.id} milestones={milestones} />
          ) : null}
          {tab === 'todos' ? <TodosCard projectId={project.id} todos={todos} /> : null}
          {tab === 'notes' ? (
            <div>
              <NotesCard projectId={project.id} notes={notes} embedded />
              <div style={{ marginTop: 16 }}>
                <CapturesCard captures={captures} embedded />
              </div>
              <div style={{ marginTop: 16 }}>
                <PullRequestsCard pullRequestItems={pullRequestItems} embedded />
              </div>
            </div>
          ) : null}
          {tab === 'status' ? (
            <StatusForm project={project} action={projectStatusPath(project.id)} />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function projectProperties(project: Project, milestones: Milestone[]): ProjectProperty[] {
  const status = project.statusNow?.trim();
  const target = formatTargetRange(milestones);
  const properties: ProjectProperty[] = [{ label: 'Status', value: status ? status : '—' }];

  if (target) {
    properties.push({ label: 'Target', value: target });
  }

  if (project.statusUpdatedAt) {
    properties.push({ label: 'Updated', value: formatRelativeTime(project.statusUpdatedAt) });
  }

  if (project.company?.name) {
    properties.push({ label: 'Company', value: project.company.name });
  }

  return properties;
}

function formatTargetRange(milestones: Milestone[]): string | null {
  const targets = milestones
    .map((milestone) => milestone.targetOn)
    .filter((value): value is string => Boolean(value))
    .sort();
  const earliest = targets[0];
  const latest = targets[targets.length - 1];

  if (!latest) {
    return null;
  } else if (earliest !== latest) {
    return `${earliest} → ${latest}`;
  } else {
    return latest;
  }
}

function projectInitials(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0);
  const first = words[0] ?? '';
  const second = words[1];

  if (second) {
    return `${first.charAt(0)}${second.charAt(0)}`.toUpperCase();
  } else {
    return first.slice(0, 2).toUpperCase();
  }
}

function PropertyFact({ label, value }: { label: string; value: string }) {
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'baseline', minWidth: 0 }}>
      <span style={{ color: 'var(--ln-faint)', fontSize: '0.75rem' }}>{label}</span>
      <span style={{ color: 'var(--ln-text)', fontSize: '0.8125rem' }}>{value}</span>
    </span>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className="ln-tab"
      data-active={active ? 'true' : 'false'}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
